/**
 * A subscription is checked out on its own, one at a time.
 *
 * That is a SERVER rule: createCheckoutPaymentIntent refuses any cart holding a
 * subscription and anything else ("Subscriptions must be purchased on their
 * own, one at a time."), because each subscription carries its own payment
 * mandate and billing cadence. It refuses the WHOLE cart, not the extra line.
 *
 * Nothing on the way to the payment step knew that. addToCart let a
 * subscription in beside other products, the cart page let the buyer press
 * Checkout, and the refusal arrived as an error on the page they had just typed
 * their card into, with nothing saying which line to remove. These two checks
 * are the cart's half of the rule: addToCart refuses the add that would break
 * it, and the cart page explains a cart that already does (carts persist with
 * no expiry, and the sign-in merge can combine two that were each fine).
 * planLinkCart in src/lib/payment-link.ts applies the same rule to payment links.
 *
 * Pure, so it runs under the node test runner.
 */

import { cartLineProductId } from './cart-line-id.ts';

/** The fields of a cart line these checks read. CartLineItem satisfies it. */
export type SubscriptionCheckLine = {
  id: string;
  tierId?: string;
  isSubscription?: boolean;
};

/**
 * Why `line` cannot join `cart`, or null when it can.
 *
 * Lines for the SAME product are not counted as "anything else": adding one is
 * a change of tier (addToCart swaps it in place), so the line is measured
 * against the cart that swap would leave. `held` is the subscription already in
 * the cart when that is the obstacle, and null when the obstacle is that `line`
 * is a subscription and the cart has other products in it.
 */
export function subscriptionAddConflict<T extends SubscriptionCheckLine>(
  cart: readonly T[],
  line: SubscriptionCheckLine,
): { held: T | null } | null {
  const productId = cartLineProductId(line.id, line.tierId);
  const others = cart.filter((item) => cartLineProductId(item.id, item.tierId) !== productId);
  const held = others.find((item) => item.isSubscription === true) ?? null;
  if (held) return { held };
  if (line.isSubscription === true && others.length > 0) return { held: null };
  return null;
}

/**
 * Whether the cart as it stands can never be checked out: a subscription with
 * anything at all beside it, including a second subscription.
 */
export function cartMixesSubscription(cart: readonly SubscriptionCheckLine[]): boolean {
  return cart.length > 1 && cart.some((item) => item.isSubscription === true);
}
